
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface SavedSearch {
  id: string;
  name: string;
  query?: string;
  location?: string;
  radius: number;
  created_at: string;
  last_used_at?: string;
  result_count?: number;
}

export interface CreateSavedSearchParams {
  name: string;
  query?: string;
  location?: string;
  radius: number;
  result_count?: number;
}

// Get all saved searches for the current user
export const getSavedSearches = async (): Promise<SavedSearch[]> => {
  try {
    // Use a more generic approach to avoid TypeScript errors with the table name
    const { data, error } = await supabase
      .from('saved_searches')
      .select('*')
      .order('last_used_at', { ascending: false })
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    // Explicitly cast the result to our SavedSearch type
    return (data as unknown as SavedSearch[]) || [];
  } catch (error) {
    console.error('Error fetching saved searches:', error);
    return [];
  }
};

// Save a new search
export const createSavedSearch = async (search: CreateSavedSearchParams): Promise<SavedSearch | null> => {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Add the user_id to the search object
    const searchWithUserId = {
      ...search,
      user_id: user.id
    };
    
    const { data, error } = await supabase
      .from('saved_searches')
      .insert(searchWithUserId)
      .select();
    
    if (error) throw error;
    if (data && data.length > 0) {
      // Cast the first result to our SavedSearch type
      return data[0] as unknown as SavedSearch;
    }
    return null;
  } catch (error) {
    console.error('Error creating saved search:', error);
    return null;
  }
};

// Update an existing saved search
export const updateSavedSearch = async (id: string, updates: Partial<SavedSearch>): Promise<SavedSearch | null> => {
  try {
    const { data, error } = await supabase
      .from('saved_searches')
      .update(updates)
      .eq('id', id)
      .select();
    
    if (error) throw error;
    if (data && data.length > 0) {
      // Cast the first result to our SavedSearch type
      return data[0] as unknown as SavedSearch;
    }
    return null;
  } catch (error) {
    console.error('Error updating saved search:', error);
    return null;
  }
};

// Update last_used_at when a saved search is used
export const markSearchAsUsed = async (id: string, resultCount?: number): Promise<void> => {
  try {
    const updates: Record<string, any> = { last_used_at: new Date().toISOString() };
    if (resultCount !== undefined) {
      updates.result_count = resultCount;
    }
    
    const { error } = await supabase
      .from('saved_searches')
      .update(updates)
      .eq('id', id);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error updating search usage:', error);
  }
};

// Delete a saved search
export const deleteSavedSearch = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('saved_searches')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting saved search:', error);
    return false;
  }
};
